import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Save, Trash2, Calendar } from "lucide-react";

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function MeetingForm() {
  const nav = useNavigate();
  const [form, setForm] = useState({ tanggal: todayIso(), shift: "2", isi_informasi: "" });
  const [submitting, setSubmitting] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/meetings");
      setMeetings(data);
    } catch {
      toast.error("Gagal memuat data meeting");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.isi_informasi.trim()) {
      toast.error("Isi Informasi wajib diisi");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/meetings", form);
      toast.success("Info meeting tersimpan");
      setForm({ tanggal: todayIso(), shift: "2", isi_informasi: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/meetings/${toDelete}`);
      toast.success("Meeting dihapus");
      setToDelete(null);
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1200px] mx-auto" data-testid="page-meeting">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-5">
        <div>
          <div className="label-ind">Briefing Operasional</div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Info Meeting</h1>
          <p className="text-sm text-slate-500 mt-1">Catatan briefing & info shift untuk arsip Google Sheets.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Form */}
        <Card className="rounded-sm bg-white border-slate-200 p-0 overflow-hidden">
          <div className="bg-slate-900 text-white px-4 py-2">
            <div className="text-xs font-bold uppercase tracking-[0.12em]">Form Info Meeting</div>
          </div>
          <form onSubmit={submit} className="p-4 space-y-4" data-testid="meeting-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-ind">Tanggal</Label>
                <Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} data-testid="meeting-tanggal" className="h-9 mt-1 rounded-sm" required />
              </div>
              <div>
                <Label className="label-ind">Shift</Label>
                <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                  <SelectTrigger className="h-9 mt-1 rounded-sm" data-testid="meeting-shift"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Shift 1</SelectItem>
                    <SelectItem value="2">Shift 2</SelectItem>
                    <SelectItem value="3">Shift 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="label-ind">Isi Informasi</Label>
              <Textarea
                value={form.isi_informasi}
                onChange={(e) => setForm({ ...form, isi_informasi: e.target.value })}
                placeholder="Tuliskan poin briefing meeting di sini..."
                rows={8}
                data-testid="meeting-isi"
                className="mt-1 rounded-sm resize-none"
                required
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting} data-testid="btn-submit-meeting" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
                <Save className="w-4 h-4 mr-1" /> {submitting ? "Menyimpan..." : "Submit"}
              </Button>
            </div>
          </form>
        </Card>

        {/* List */}
        <Card className="rounded-sm bg-white border-slate-200 p-0 overflow-hidden">
          <div className="bg-slate-900 text-white px-4 py-2 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.12em]">Daftar Info Meeting</div>
            <span className="text-[11px] font-mono text-slate-300">{meetings.length} entri</span>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {loading && <div className="p-6 text-sm text-slate-500">Memuat...</div>}
            {!loading && meetings.length === 0 && (
              <div className="p-6 text-sm text-slate-400 text-center">Belum ada info meeting.</div>
            )}
            {meetings.slice().reverse().map((m) => (
              <div key={m.id} className="p-4 border-b border-slate-100 hover:bg-slate-50" data-testid={`meeting-row-${m.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 text-xs">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-mono text-slate-700">{m.tanggal}</span>
                    <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 font-semibold rounded-sm">Shift {m.shift}</span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => setToDelete(m.id)} data-testid={`btn-delete-meeting-${m.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">{m.isi_informasi}</div>
                <div className="text-[10px] text-slate-400 font-mono mt-1">{m.id}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-meeting-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Info Meeting?</AlertDialogTitle>
            <AlertDialogDescription>
              Aksi ini tidak dapat dibatalkan. Catatan akan terhapus dari Google Sheets.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirm-delete-meeting">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
